import { Box, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { AxiosResponse } from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../../context/ApiProvider';
import { PaginationInterface } from '../../../../../../shared/type/paginationInterface.type';
import {
  TransactionHistoryLogInterface,
  TransactionHistoryInterfaceMonth,
  TransactionHistoryInterface
} from '../../../../../../shared/type/transactionHistoryInterface.type';
import CustomChakraButton from '../../../../../CustomChakraButton/CustomChakraButton';
import Pagination from '../../../../../Pagination/Pagination';
import AddCustomOperation from '../../../BalanceHistory/AddCustomOperation/AddCustomOperation';
import BalanceHistoryDetails from './BalanceHistoryDetails/BalanceHistoryDetails';
import { StickyScrollableTableContainer } from '../../../../../DataTable/StickyScrollableTableContainer';

type BalanceHistoryDetailsPopUpType = {
  show: boolean;
  paymentId?: number;
};

const KidBalanceHistory = () => {
  const { apiConsumerFinancialHistoryController } = useApi();
  const { kidId } = useParams();
  const columnHelper = createColumnHelper<TransactionHistoryLogInterface>();

  const [isLoading, setIsLoading] = useState(false);
  const [balanceHistory, setBalanceHistory] = useState<TransactionHistoryInterfaceMonth[]>([]);
  const [showAddOperation, setShowAddOperation] = useState(false);
  const [balanceHistoryDetailsPopUp, setBalanceHistoryDetailsPopUp] =
    useState<BalanceHistoryDetailsPopUpType>({
      show: false,
      paymentId: undefined
    });
  const [actualPage, setActualPage] = useState<PaginationInterface>({
    totalCount: 0,
    hasNextPage: false,
    next: { limit: 0, offset: 0 }
  });
  const [page, setPage] = useState({
    pageNumber: 1,
    pageSize: 30,
    maxPageNumber: 0
  });

  const fetchBalanceHistory = async () => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<TransactionHistoryInterface> =
        await apiConsumerFinancialHistoryController('consumer-transactions').get('', {
          params: {
            consumerId: kidId,
            pageNumber: page.pageNumber,
            pageSize: page.pageSize
          }
        });

      const { data, next, hasNextPage, totalCount } = response.data;
      setBalanceHistory(data);
      setActualPage({ next, hasNextPage, totalCount });
      setPage((prev) => ({
        ...prev,
        maxPageNumber: Math.ceil(totalCount / page.pageSize)
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (pageNum: number) => {
    setPage((prev) => ({ ...prev, pageNumber: pageNum }));
  };

  useEffect(() => {
    fetchBalanceHistory();
  }, [kidId, page.pageNumber]);

  const columns = [
    columnHelper.accessor('when', {
      header: 'Data operacji',
      size: 90,
      cell: (info) => {
        const when = info.getValue();
        return <p className="text-grayLight-900 font-medium text-sm w-[72px] text-wrap">{when}</p>;
      }
    }),
    columnHelper.accessor('description', {
      header: 'Opis operacji',
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 text-wrap">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('amount', {
      header: 'Kwota',
      size: 90,
      cell: (info) => {
        const { amount, creditDebit } = info.row.original;
        return (
          <p className="text-sm font-medium text-grayLight-900 text-right w-[80px]">{creditDebit ? "-":" "}{amount}</p>
        );
      }
    }),
    columnHelper.accessor('saldoBeforeOperation', {
      header: 'Saldo przed',
      size: 90,
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 text-right w-[80px]">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('saldoAfterOperation', {
      header: 'Saldo po',
      size: 90,
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 text-right w-[80px]">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('performedByUser.lastName', {
      header: 'Wykonana przez',
      cell: (info) => {
        const { lastName, firstName, admin } = info.row.original.performedByUser;
        return (
          <p className="text-sm font-medium text-grayLight-900 w-44">
            {admin ? 'ADMIN: ' : ''}
            {lastName} {firstName}
          </p>
        );
      }
    }),
    columnHelper.accessor('when', {
      header: '',
      size: 120,
      cell: (info) => {
        const { paymentId } = info.row.original;

        if (!paymentId) return;

        return (
          <div className="flex gap-spacing-sm w-[60px]">
            <button
              onClick={() => {
                setBalanceHistoryDetailsPopUp({
                  show: true,
                  paymentId: paymentId
                });
              }}
              className="font-semibold text-sm text-grayLight-600 bg-transparent border-none">
              Szczegóły
            </button>
          </div>
        );
      }
    })
  ];

  return (
    <section className="p-spacing-xl w-full">
      <Box mb={6} className="flex items-center justify-between">
        <Text fontSize="xl" fontWeight="semibold" color="gray.900">
          Historia salda
        </Text>
        <CustomChakraButton
          size="md"
          hierarchy="secondaryColor"
          iconPosition="left"
          icon="plus"
          buttonProps={{
            onClick: () => setShowAddOperation(true)
          }}>
          Edycja salda
        </CustomChakraButton>
      </Box>

      <StickyScrollableTableContainer
        columns={columns}
        data={balanceHistory}
        isLoading={isLoading}
        enableSearch={false}
        showClearFilters={false}
      />

      <Box pt={4} pb={6}>
        <Pagination
          pageNumber={page.pageNumber}
          maxPageNumber={page.maxPageNumber}
          hasNextPage={actualPage.hasNextPage}
          onPageChange={handlePageChange}
        />
      </Box>

      {balanceHistoryDetailsPopUp.show && (
        <BalanceHistoryDetails
          paymentId={balanceHistoryDetailsPopUp.paymentId}
          isOpen={balanceHistoryDetailsPopUp.show}
          onClose={() => setBalanceHistoryDetailsPopUp({ show: false, paymentId: undefined })}
        />
      )}

      {showAddOperation && (
        <AddCustomOperation
          consumerId={Number(kidId)}
          isOpen={showAddOperation}
          currentSaldo={balanceHistory[0]?.saldoAfterOperation}
          handleClose={() => {
            setShowAddOperation(false);
            fetchBalanceHistory();
          }}
        />
      )}
    </section>
  );
};

export default KidBalanceHistory;
